import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import { AdjustColorTint, HexToRgba, colors } from 'utils/colors'
import { WidthProps } from 'types/styled'
import Icon from '../../Icon'
import Text from '../../Text'
import TextButton from '../../Buttons/TextButton'
import { variants } from '../variants'
import { isLegacy, LeadDetailsDesignVersion } from '../../utils/designVersionHelpers'

type InlineNotificationProps = {
  message: string | React.ReactNode
  variant: 'error' | 'info' | 'success' | 'warning'
  width?: WidthProps | string
  closeable?: boolean
  onClose?: () => void
  customClose?: React.ReactNode
  classPrefix?: string
  actionIcon?: string | string[]
  actionLabel?: string
  footerActionLabel?: string
  action?: () => void
  footerAction?: () => void
  role?: string
  isContained?: boolean
  isDense?: boolean
  hideColor?: boolean
  designVersion?: LeadDetailsDesignVersion
} & SpaceProps

const InlineNotification = (props: InlineNotificationProps) => {
  const {
    message,
    variant,
    width,
    closeable,
    onClose,
    customClose,
    classPrefix,
    action,
    footerAction,
    actionIcon,
    actionLabel,
    footerActionLabel,
    role,
    designVersion = 'v1',
    isContained = true,
    isDense = false,
    hideColor = false,
    ...spaceProps
  } = props
  const cssClassName = classPrefix ? `${classPrefix}-inline-notification` : 'inline-notification'

  const [display, setDisplay] = useState<boolean>(message ? true : false)
  const selectedVariant = variants.find((v) => v.name === variant) || {
    color: 'gray7',
    icon: ['far', 'info-circle'],
    name: 'info',
  }
  const containerRef = useRef<HTMLDivElement>(null)

  const baseIconProps = {
    color: selectedVariant.color,
    fontSize: 16,
    icon: selectedVariant.icon,
    my: isDense ? 0 : 12,
  }
  const styledIconProps = !isLegacy(designVersion)
    ? { ...baseIconProps, mr: '8px' }
    : { ...baseIconProps, mr: '0.5rem' }

  const handleCloseBanner = () => {
    setTimeout(() => {
      setDisplay(false)
    }, 350)

    closeBannerAnimation()
    onClose?.()
  }

  const closeBannerAnimation = () => {
    const ref = containerRef.current
    if (!ref) return

    ref.style.opacity = '0'
    ref.style.overflow = 'hidden'
    ref.style.height = '0px'
  }

  useEffect(() => {
    if (message !== '') {
      setDisplay(true)
    }
  }, [message])

  return display ? (
    <Container
      role={role}
      designVersion={designVersion}
      color={hideColor ? 'white' : selectedVariant.color}
      isContained={isContained}
      isDense={isDense}
      width={width}
      ref={containerRef}
      className={cssClassName}
      {...spaceProps}
    >
      <ContentWrapper>
        <StyledIcon {...styledIconProps} />
        <StyledText
          color={hideColor ? 'disabledText' : selectedVariant.color}
          fontSize={13}
          fontWeight={hideColor ? undefined : 'bold'}
          my={isDense ? 0 : 13}
        >
          {message}
        </StyledText>
        {action && actionLabel && (
          <ActionButton color={colors[selectedVariant.color]} icon={actionIcon} onClick={() => action?.()}>
            {actionLabel}
          </ActionButton>
        )}
      </ContentWrapper>
      {footerAction && footerActionLabel && (
        <ActionButton color={colors[selectedVariant.color]} icon={actionIcon} onClick={() => footerAction?.()}>
          {footerActionLabel}
        </ActionButton>
      )}
      {customClose && <div onClick={handleCloseBanner}>{customClose}</div>}
      {closeable && (
        <StyledIcon
          color={selectedVariant.color}
          fontSize={16}
          icon={['far', 'times']}
          mx={15}
          my={12}
          onClick={handleCloseBanner}
        />
      )}
    </Container>
  ) : null
}

const Container = styled.div<
  {
    color: keyof typeof colors
    isContained: boolean
    isDense: boolean
    width?: WidthProps | string
    designVersion: LeadDetailsDesignVersion
  } & SpaceProps
>`
  background-color: ${(p) => HexToRgba(colors[p.color], 0.1)};
  border: ${(p) => (p.isContained ? `1px solid ${colors[p.color]}` : 'none')};
  border-radius: ${(p) => (p.isContained ? '3px' : '0')};
  display: flex;
  min-height: ${(p) => (p.isDense ? '30px' : '40px')};
  justify-content: space-between;
  align-items: center;
  min-width: 250px;
  width: ${(p) => (p.width ? `calc(${p.width} - 14px)` : p.isContained ? '700px' : '100%')};
  opacity: 1;
  transition: height 0.4s ease-out, opacity 0.4s linear;
  padding: ${(p) => (!isLegacy(p.designVersion) ? `8px 16px` : '0.5rem 1rem')};
  user-select: none;
  ${space}
`

const StyledIcon = styled((p) => <Icon {...p} />)`
  color: ${({ color }: { color: keyof typeof colors }) => AdjustColorTint(colors[color], 10)};
  display: inline-block;
`

const StyledText = styled((p) => <Text {...p} />)`
  color: ${({ color }: { color: keyof typeof colors }) => AdjustColorTint(colors[color], 10)};
  text-align: left;
`

const ActionButton = styled((p) => <TextButton {...p} />)`
  margin-left: 24px;
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 0;
`

export default InlineNotification
